/**
 * AdminUsers — Phase A-3.
 * Liste paginée + filtres + modal détail (4 onglets) + modal create.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, Search, UserPlus, ChevronLeft, ChevronRight,
  Eye, Pencil, UserX, UserCheck, KeyRound, Trash2, X, Copy,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { hasRole } from "@/components/admin/RoleGuard";
import {
  fetchUsers, fetchUserDetail, patchUser, deleteUser, createUser, resetUserPassword,
} from "@/lib/admin-api";

const roleColor = {
  super_admin: "bg-red-700 text-white",
  admin: "bg-orange-600 text-white",
  analyst: "bg-blue-600 text-white",
  viewer: "bg-gray-500 text-white",
  researcher: "bg-violet-600 text-white",
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const fmtRelative = (iso) => {
  if (!iso) return "Jamais";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
};
const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
};

function RoleBadge({ role }) {
  if (!role) return <Badge variant="outline">user</Badge>;
  return <Badge className={roleColor[role] || "bg-slate-500 text-white"}>{role}</Badge>;
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const isSuper = hasRole(currentUser, "super_admin");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [filters, setFilters] = useState({ search: "", role: "", status: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;
      const { data, total } = await fetchUsers(params);
      setRows(data || []);
      setTotal(total || 0);
    } catch {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  async function toggleSuspend(u) {
    const suspending = !u.suspended_at;
    if (suspending && !confirm(`Suspendre ${u.email} ? Il sera déconnecté immédiatement.`)) return;
    try {
      await patchUser(u.id, { suspended: suspending });
      toast.success(suspending ? "Compte suspendu." : "Compte réactivé.");
      load();
    } catch { toast.error("Échec."); }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="admin-users-page">
      <div className="flex items-center gap-2">
        <Users className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Utilisateurs</h1>
        <span className="ml-auto text-sm text-muted-foreground">{total.toLocaleString("fr-FR")} comptes</span>
        <Button onClick={() => setShowCreate(true)} data-testid="users-create-btn">
          <UserPlus className="mr-2 h-4 w-4" /> Créer
        </Button>
      </div>

      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 md:grid-cols-5">
          <Input placeholder="Rechercher (nom, email, organisation)" value={filters.search}
            onChange={e => setFilters(f => ({...f, search: e.target.value}))}
            data-testid="users-filter-search" />
          <select className="rounded border px-2 text-sm" value={filters.role}
            onChange={e => setFilters(f => ({...f, role: e.target.value}))}
            data-testid="users-filter-role">
            <option value="">Tous les rôles</option>
            <option value="super_admin">super_admin</option>
            <option value="admin">admin</option>
            <option value="analyst">analyst</option>
            <option value="viewer">viewer</option>
            <option value="researcher">researcher</option>
          </select>
          <select className="rounded border px-2 text-sm" value={filters.status}
            onChange={e => setFilters(f => ({...f, status: e.target.value}))}
            data-testid="users-filter-status">
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="suspended">Suspendu</option>
            <option value="never_logged">Jamais connecté</option>
          </select>
          <Button onClick={() => { setPage(1); load(); }} disabled={loading} data-testid="users-apply">
            <Search className="mr-1 h-4 w-4" /> Filtrer
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
                <th className="px-3 py-2">Utilisateur</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Rôle</th>
                <th className="px-3 py-2 text-right">Projets</th>
                <th className="px-3 py-2">Dernière connexion</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const initials = (u.name || u.email || "?").split(/[\s@]+/).map(x=>x[0]).filter(Boolean).slice(0,2).join("").toUpperCase();
                const status = u.suspended_at ? "Suspendu" : u.last_login_at ? "Actif" : "Jamais connecté";
                return (
                  <tr key={u.id} className="border-b text-xs" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[10px] font-bold">
                          {initials}
                        </div>
                        <div>
                          <div className="font-semibold">{u.name || "—"}</div>
                          {u.organization && <div className="text-[10px] text-muted-foreground">{u.organization}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono">{u.email}</td>
                    <td className="px-3 py-2"><RoleBadge role={u.role} /></td>
                    <td className="px-3 py-2 text-right font-mono">{u.projects_count}</td>
                    <td className="px-3 py-2">{fmtRelative(u.last_login_at)}</td>
                    <td className="px-3 py-2">
                      {status === "Actif" && <Badge className="bg-emerald-600 text-white">✅ Actif</Badge>}
                      {status === "Suspendu" && <Badge className="bg-red-700 text-white">⛔ Suspendu</Badge>}
                      {status === "Jamais connecté" && <Badge variant="outline">🔵 Jamais</Badge>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setSelectedId(u.id)} data-testid={`user-view-${u.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleSuspend(u)} data-testid={`user-suspend-${u.id}`}>
                          {u.suspended_at ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Aucun utilisateur.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" disabled={page<=1} onClick={() => setPage(p => p-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm">Page {page} / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={page>=totalPages} onClick={() => setPage(p => p+1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {selectedId && (
        <UserDetailModal
          id={selectedId}
          isSuper={isSuper}
          onClose={() => setSelectedId(null)}
          onChange={load}
        />
      )}
      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

/* --- DETAIL MODAL avec 4 onglets --- */
function UserDetailModal({ id, isSuper, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [tempPwd, setTempPwd] = useState(null);

  async function load() {
    try { const d = await fetchUserDetail(id); setData(d); setForm(d?.profile || {}); }
    catch { toast.error("Erreur."); onClose(); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function save() {
    try {
      const payload = {
        name: form.name,
        organization: form.organization,
        country: form.country,
      };
      if (isSuper && form.role && form.role !== data.profile.role) payload.role = form.role;
      await patchUser(id, payload);
      toast.success("Profil mis à jour.");
      setEditing(false); load(); onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }

  async function onResetPwd() {
    if (!confirm("Réinitialiser le mot de passe ? L'utilisateur sera déconnecté.")) return;
    try { const r = await resetUserPassword(id); setTempPwd(r.temporary_password); toast.success("Mot de passe réinitialisé."); }
    catch { toast.error("Échec."); }
  }
  async function onDelete() {
    const txt = prompt('Tapez "SUPPRIMER" pour confirmer la suppression définitive (anonymisée RGPD).');
    if (txt !== "SUPPRIMER") return;
    try { await deleteUser(id); toast.success("Utilisateur supprimé (anonymisé)."); onChange?.(); onClose(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }

  if (!data) return null;
  const { profile, activity, sessions, quotas } = data;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="user-detail-modal">
        <DialogHeader>
          <DialogTitle>{profile.name || profile.email}</DialogTitle>
          <DialogDescription>{profile.email}</DialogDescription>
          <div className="pt-1"><RoleBadge role={profile.role} /></div>
        </DialogHeader>

        <div className="flex gap-2 border-b pb-2">
          {[["profile","Profil"],["activity","Activité"],["sessions","Sessions"],["quotas","Quotas"]].map(([k,l])=>(
            <button key={k} onClick={() => setTab(k)}
              className={`rounded px-3 py-1 text-sm ${tab===k?"bg-accent font-semibold":"hover:bg-muted"}`}
              data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "profile" && (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom</Label><Input value={form.name||""} disabled={!editing} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
              <div><Label>Email</Label><Input value={form.email||""} disabled /></div>
              <div><Label>Organisation</Label><Input value={form.organization||""} disabled={!editing} onChange={e=>setForm(f=>({...f,organization:e.target.value}))} /></div>
              <div><Label>Pays</Label><Input value={form.country||""} disabled={!editing} onChange={e=>setForm(f=>({...f,country:e.target.value}))} /></div>
              <div>
                <Label>Rôle {isSuper ? "" : "(super_admin requis)"}</Label>
                <select className="w-full rounded border px-2 py-1 text-sm" value={form.role||""} disabled={!editing || !isSuper}
                  onChange={e=>setForm(f=>({...f,role:e.target.value}))} data-testid="role-select">
                  <option value="viewer">viewer</option>
                  <option value="analyst">analyst</option>
                  <option value="researcher">researcher</option>
                  <option value="admin">admin</option>
                  <option value="super_admin">super_admin</option>
                </select>
              </div>
              <div><Label>Créé le</Label><Input value={fmtDate(profile.created_at)} disabled /></div>
            </div>
            <div className="flex gap-2">
              {!editing && <Button onClick={()=>setEditing(true)} data-testid="edit-btn"><Pencil className="mr-1 h-4 w-4"/>Modifier</Button>}
              {editing && <><Button onClick={save} data-testid="save-btn">Enregistrer</Button>
                <Button variant="outline" onClick={()=>{setEditing(false);setForm(profile);}}>Annuler</Button></>}
              <Button variant="outline" onClick={onResetPwd} data-testid="reset-pwd-btn">
                <KeyRound className="mr-1 h-4 w-4"/>Réinitialiser mot de passe
              </Button>
              {isSuper && (
                <Button variant="destructive" className="ml-auto" onClick={onDelete} data-testid="delete-btn">
                  <Trash2 className="mr-1 h-4 w-4"/>Supprimer
                </Button>
              )}
            </div>
            {tempPwd && (
              <div className="rounded border border-amber-400 bg-amber-50 p-3 text-xs dark:bg-amber-950/20">
                <div className="font-bold">Mot de passe temporaire (affiché une seule fois)</div>
                <div className="mt-1 flex items-center gap-2">
                  <code className="rounded bg-background px-2 py-1">{tempPwd}</code>
                  <Button size="sm" variant="outline" onClick={()=>{navigator.clipboard.writeText(tempPwd); toast.success("Copié");}}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "activity" && (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-4 gap-3 text-center">
              <StatCard label="Projets" value={activity.projects_count}/>
              <StatCard label="Analyses" value={activity.analyses_count}/>
              <StatCard label="Rapports" value={activity.reports_count}/>
              <StatCard label="LLM (mois)" value={activity.llm_calls_this_month}/>
            </div>
            <div className="text-xs text-muted-foreground">Stockage utilisé : {fmtBytes(activity.storage_bytes)}</div>
            <div>
              <div className="mb-1 text-sm font-bold">10 dernières actions</div>
              <table className="w-full text-xs">
                <tbody>
                  {activity.recent_events.map((r,i)=>(
                    <tr key={i} className="border-b">
                      <td className="px-2 py-1 font-mono">{new Date(r.created_at).toLocaleString("fr-FR")}</td>
                      <td className="px-2 py-1 font-mono">{r.action}</td>
                      <td className="px-2 py-1">{r.status}</td>
                    </tr>
                  ))}
                  {activity.recent_events.length === 0 && <tr><td className="px-2 py-2 text-muted-foreground">Aucune activité.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "sessions" && (
          <div className="space-y-2 py-2">
            <Button variant="destructive" size="sm"
              onClick={async()=>{if(confirm("Révoquer toutes les sessions ?")){await patchUser(id,{suspended:true}); await patchUser(id,{suspended:false}); load();}}}
              data-testid="revoke-all-btn">Révoquer TOUTES les sessions</Button>
            <table className="w-full text-xs">
              <thead><tr className="border-b text-left uppercase"><th className="px-2 py-1">IP</th><th className="px-2 py-1">Agent</th><th className="px-2 py-1">Dernière activité</th></tr></thead>
              <tbody>
                {sessions.map(s=>(
                  <tr key={s.id} className="border-b">
                    <td className="px-2 py-1 font-mono">{s.ip_address||"—"}</td>
                    <td className="px-2 py-1 text-[10px]">{(s.user_agent||"").slice(0,50)}</td>
                    <td className="px-2 py-1 font-mono">{new Date(s.last_active).toLocaleString("fr-FR")}</td>
                  </tr>
                ))}
                {sessions.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-muted-foreground">Aucune session active.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "quotas" && (
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label>Max projets</Label>
              <Input type="number" defaultValue={quotas.max_projects ?? ""}
                onBlur={e => patchUser(id, { quota_max_projects: Number(e.target.value) }).then(() => { toast.success("Quota projets mis à jour."); load(); })}
                data-testid="quota-projects-input" />
            </div>
            <div>
              <Label>Max appels LLM / jour</Label>
              <Input type="number" defaultValue={quotas.max_llm_calls_per_day ?? ""}
                onBlur={e => patchUser(id, { quota_max_llm_per_day: Number(e.target.value) }).then(() => { toast.success("Quota LLM mis à jour."); load(); })}
                data-testid="quota-llm-input" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="mr-1 h-4 w-4"/>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({label,value}) {
  return (
    <div className="rounded border bg-muted/30 p-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="font-mono text-xl font-bold">{value ?? "—"}</div>
    </div>
  );
}

/* --- CREATE MODAL --- */
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", organization: "", country: "", role: "viewer", send_welcome_email: true });
  const [tempPwd, setTempPwd] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      const d = await createUser(form);
      setTempPwd(d.temporary_password);
      toast.success("Utilisateur créé.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
    finally { setBusy(false); }
  }

  if (tempPwd) {
    return (
      <Dialog open onOpenChange={()=>{onCreated?.();}}>
        <DialogContent data-testid="user-create-success">
          <DialogHeader><DialogTitle>Utilisateur créé</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="rounded border border-amber-400 bg-amber-50 p-3 text-sm dark:bg-amber-950/20">
              <strong>Mot de passe temporaire (affiché une seule fois) :</strong>
              <div className="mt-1 flex items-center gap-2">
                <code className="rounded bg-background px-2 py-1">{tempPwd}</code>
                <Button size="sm" onClick={()=>{navigator.clipboard.writeText(tempPwd); toast.success("Copié");}}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="mt-2 text-xs">L'utilisateur devra changer son mot de passe à la première connexion.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={()=>onCreated?.()}>Terminer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="user-create-modal">
        <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nom complet *</Label>
            <Input required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} data-testid="create-name" /></div>
          <div><Label>Email *</Label>
            <Input required type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} data-testid="create-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Organisation</Label><Input value={form.organization} onChange={e=>setForm(f=>({...f,organization:e.target.value}))} /></div>
            <div><Label>Pays</Label><Input value={form.country} onChange={e=>setForm(f=>({...f,country:e.target.value}))} /></div>
          </div>
          <div>
            <Label>Rôle initial</Label>
            <select className="w-full rounded border px-2 py-1 text-sm" value={form.role}
              onChange={e=>setForm(f=>({...f,role:e.target.value}))} data-testid="create-role">
              <option value="viewer">viewer</option>
              <option value="analyst">analyst</option>
              <option value="researcher">researcher</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_welcome_email}
              onChange={e=>setForm(f=>({...f,send_welcome_email:e.target.checked}))} />
            Envoyer email de bienvenue (disponible après Phase A-6)
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="create-submit">
              {busy ? "Création…" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
